/*
 * The rule behind a Timeline's collapsed runs, kept apart from the component so it can be checked
 * without a browser. A long stretch of changes with no comment between them (a ticket dragged
 * across the Board, retitled twice, reassigned) says little one by one, so a run of four or more
 * shows its first and last, and a "Show n more" between them opens the rest.
 */

/** A run collapses once it holds this many changes. */
export const collapsesFrom = 4

/**
 * One line of a Timeline as shown: an entry, or the button standing in for the `hidden` middle of
 * a run, named `run` after the run's first entry.
 */
export type ShownLine<Entry> =
  { kind: "entry"; entry: Entry } | { kind: "more"; run: string; hidden: number }

/**
 * The lines a Timeline shows, in order. With `collapseRuns`, each run of `collapsesFrom` or more
 * consecutive events keeps its first and last, and the rest become one "more" line, unless the
 * Member has already opened that run (its name is in `opened`). Comments are never collapsed, and
 * they end a run.
 */
export function shownLines<Entry extends { kind: "event" | "comment"; id: string }>(
  entries: readonly Entry[],
  { collapseRuns, opened }: { collapseRuns: boolean; opened: ReadonlySet<string> },
): ShownLine<Entry>[] {
  const lines: ShownLine<Entry>[] = []
  let run: Entry[] = []
  const endRun = () => {
    const [first, last] = [run[0], run.at(-1)]
    if (collapseRuns && first && last && run.length >= collapsesFrom && !opened.has(first.id)) {
      lines.push({ kind: "entry", entry: first })
      lines.push({ kind: "more", run: first.id, hidden: run.length - 2 })
      lines.push({ kind: "entry", entry: last })
    } else lines.push(...run.map((entry) => ({ kind: "entry" as const, entry })))
    run = []
  }
  for (const entry of entries) {
    if (entry.kind === "event") {
      run.push(entry)
      continue
    }
    endRun()
    lines.push({ kind: "entry", entry })
  }
  endRun()
  return lines
}
